<script lang="ts">
	import { tick } from 'svelte';
	import { goto } from '$app/navigation';
	import { apiGet, ApiError } from '$lib/api';
	import {
		Search,
		LayoutDashboard,
		Sword,
		PawPrint,
		BookOpen,
		Users,
		Terminal,
		Package,
		Globe,
		Archive,
		Map as MapIcon,
		Settings,
		FileText
	} from '@lucide/svelte';

	// Ctrl/Cmd+K command palette: navigate to any tab of either game, or
	// search the wiki. Wired from +layout.svelte, which owns the global
	// keydown listener and passes `open` as a bindable.
	let { open = $bindable(false) }: { open?: boolean } = $props();

	type Command = { id: string; label: string; hint: string; icon: typeof Search; go: string };

	const staticCommands: Command[] = [
		{ id: 'home', label: 'Vue d’ensemble', hint: 'Accueil', icon: LayoutDashboard, go: '/' },
		{
			id: 'v-apercu',
			label: 'Valheim — Aperçu',
			hint: 'Valheim',
			icon: Sword,
			go: '/servers/valheim?tab=apercu'
		},
		{
			id: 'v-joueurs',
			label: 'Valheim — Joueurs',
			hint: 'Valheim',
			icon: Users,
			go: '/servers/valheim?tab=joueurs'
		},
		{
			id: 'v-console',
			label: 'Valheim — Console',
			hint: 'Valheim',
			icon: Terminal,
			go: '/servers/valheim?tab=console'
		},
		{
			id: 'v-mods',
			label: 'Valheim — Mods',
			hint: 'Valheim',
			icon: Package,
			go: '/servers/valheim?tab=mods'
		},
		{
			id: 'v-monde',
			label: 'Valheim — Monde',
			hint: 'Valheim',
			icon: Globe,
			go: '/servers/valheim?tab=monde'
		},
		{
			id: 'v-sauvegardes',
			label: 'Valheim — Sauvegardes',
			hint: 'Valheim',
			icon: Archive,
			go: '/servers/valheim?tab=sauvegardes'
		},
		{
			id: 'v-carte',
			label: 'Valheim — Carte',
			hint: 'Valheim',
			icon: MapIcon,
			go: '/servers/valheim?tab=carte'
		},
		{ id: 'v-wiki', label: 'Valheim — Wiki', hint: 'Valheim', icon: BookOpen, go: '/wiki/valheim' },
		{
			id: 'p-apercu',
			label: 'Palworld — Aperçu',
			hint: 'Palworld',
			icon: PawPrint,
			go: '/servers/palworld?tab=apercu'
		},
		{
			id: 'p-joueurs',
			label: 'Palworld — Joueurs',
			hint: 'Palworld',
			icon: Users,
			go: '/servers/palworld?tab=joueurs'
		},
		{
			id: 'p-console',
			label: 'Palworld — Console',
			hint: 'Palworld',
			icon: Terminal,
			go: '/servers/palworld?tab=console'
		},
		{
			id: 'p-configuration',
			label: 'Palworld — Configuration',
			hint: 'Palworld',
			icon: Settings,
			go: '/servers/palworld?tab=configuration'
		},
		{
			id: 'p-sauvegardes',
			label: 'Palworld — Sauvegardes',
			hint: 'Palworld',
			icon: Archive,
			go: '/servers/palworld?tab=sauvegardes'
		},
		{
			id: 'p-carte',
			label: 'Palworld — Carte',
			hint: 'Palworld',
			icon: MapIcon,
			go: '/servers/palworld?tab=carte'
		},
		{
			id: 'p-wiki',
			label: 'Palworld — Wiki',
			hint: 'Palworld',
			icon: BookOpen,
			go: '/wiki/palworld'
		}
	];

	type WikiResult = {
		game: string;
		slug: string;
		title: string;
		snippet: string;
		category: string;
	};

	let query = $state('');
	let wikiResults = $state<WikiResult[]>([]);
	let searching = $state(false);
	let selected = $state(0);
	let inputEl = $state<HTMLInputElement | undefined>(undefined);

	const filteredCommands = $derived(
		query.trim()
			? staticCommands.filter((c) => c.label.toLowerCase().includes(query.trim().toLowerCase()))
			: staticCommands
	);

	type Entry = { kind: 'command'; command: Command } | { kind: 'wiki'; result: WikiResult };

	const entries: Entry[] = $derived([
		...filteredCommands.map((command): Entry => ({ kind: 'command', command })),
		...wikiResults.map((result): Entry => ({ kind: 'wiki', result }))
	]);

	// Debounced wiki search -- reads `query` (write) into `wikiResults`
	// (different state var), so no read+write-same-state loop risk.
	$effect(() => {
		const q = query.trim();
		if (q.length < 2) {
			wikiResults = [];
			return;
		}
		searching = true;
		const timer = setTimeout(async () => {
			try {
				const res = await apiGet<{ results: WikiResult[] }>(
					`/api/wiki/search?q=${encodeURIComponent(q)}`
				);
				wikiResults = res.results;
			} catch (e) {
				if (!(e instanceof ApiError)) console.warn(e);
				wikiResults = [];
			} finally {
				searching = false;
			}
		}, 300);
		return () => clearTimeout(timer);
	});

	$effect(() => {
		if (entries.length === 0) selected = 0;
		else if (selected >= entries.length) selected = entries.length - 1;
	});

	$effect(() => {
		if (open) {
			query = '';
			selected = 0;
			tick().then(() => inputEl?.focus());
		}
	});

	function close() {
		open = false;
	}

	function run(entry: Entry) {
		close();
		if (entry.kind === 'command') goto(entry.command.go);
		else goto(`/wiki/${entry.result.game}/${entry.result.slug}`);
	}

	function onKeydown(e: KeyboardEvent) {
		if (e.key === 'Escape') {
			e.preventDefault();
			close();
		} else if (e.key === 'ArrowDown') {
			e.preventDefault();
			if (entries.length) selected = (selected + 1) % entries.length;
		} else if (e.key === 'ArrowUp') {
			e.preventDefault();
			if (entries.length) selected = (selected - 1 + entries.length) % entries.length;
		} else if (e.key === 'Enter') {
			e.preventDefault();
			const entry = entries[selected];
			if (entry) run(entry);
		}
	}

	function stripTags(html: string) {
		return html.replace(/<\/?mark>/g, '');
	}
</script>

{#if open}
	<div class="backdrop" onclick={close} role="presentation">
		<div
			class="palette"
			role="dialog"
			aria-modal="true"
			aria-label="Palette de commandes"
			tabindex="-1"
			onclick={(e) => e.stopPropagation()}
			onkeydown={onKeydown}
		>
			<div class="search-row">
				<Search size={16} />
				<label class="sr-only" for="palette-input">Rechercher une action ou une page wiki</label>
				<input
					id="palette-input"
					bind:this={inputEl}
					bind:value={query}
					type="text"
					placeholder="Naviguer, rechercher dans le wiki…"
					autocomplete="off"
					role="combobox"
					aria-expanded="true"
					aria-controls="palette-listbox"
					aria-activedescendant={entries[selected] ? `palette-opt-${selected}` : undefined}
				/>
				<kbd>Échap</kbd>
			</div>
			<ul id="palette-listbox" role="listbox" aria-label="Résultats">
				{#each filteredCommands as command, i (command.id)}
					<li>
						<button
							id="palette-opt-{i}"
							role="option"
							aria-selected={selected === i}
							class:active={selected === i}
							onclick={() => run({ kind: 'command', command })}
							onmouseenter={() => (selected = i)}
						>
							<command.icon size={15} />
							<span class="label">{command.label}</span>
							<span class="hint">{command.hint}</span>
						</button>
					</li>
				{/each}
				{#if wikiResults.length}
					<li class="section-label" aria-hidden="true">Wiki</li>
					{#each wikiResults as result, j (result.game + result.slug)}
						{@const i = filteredCommands.length + j}
						<li>
							<button
								id="palette-opt-{i}"
								role="option"
								aria-selected={selected === i}
								class:active={selected === i}
								onclick={() => run({ kind: 'wiki', result })}
								onmouseenter={() => (selected = i)}
							>
								<FileText size={15} />
								<span class="label">
									{result.title}
									<span class="snippet">{stripTags(result.snippet)}</span>
								</span>
								<span class="hint">{result.game}</span>
							</button>
						</li>
					{/each}
				{/if}
				{#if entries.length === 0 && !searching}
					<li class="empty">Aucun résultat.</li>
				{/if}
			</ul>
		</div>
	</div>
{/if}

<style>
	.backdrop {
		position: fixed;
		inset: 0;
		background: rgba(0, 0, 0, 0.4);
		display: flex;
		align-items: flex-start;
		justify-content: center;
		padding-top: 12vh;
		z-index: 200;
		animation: fade var(--dur-fast) var(--ease-out);
	}
	.palette {
		width: min(560px, 92vw);
		max-height: 70vh;
		display: flex;
		flex-direction: column;
		background: var(--surface-raised);
		border: 1px solid var(--border);
		border-radius: var(--radius-lg);
		box-shadow: var(--shadow-lg);
		overflow: hidden;
		animation: rise var(--dur-base) var(--ease-out);
	}
	.search-row {
		display: flex;
		align-items: center;
		gap: var(--space-2);
		padding: var(--space-3) var(--space-4);
		border-bottom: 1px solid var(--border);
		color: var(--text-muted);
	}
	/* L'input lui-même n'a pas d'anneau de focus visible (outline: none plus
	   bas, pour éviter un double contour dans ce champ sans bordure propre) --
	   le remplacement exigé par les consignes d'accessibilité (jamais de
	   outline-none sans équivalent) porte sur le conteneur via :focus-within. */
	.search-row:focus-within {
		box-shadow: inset 0 -2px 0 var(--accent);
	}
	input {
		flex: 1;
		font: inherit;
		font-size: var(--text-md);
		border: none;
		background: none;
		color: var(--text-primary);
		outline: none;
	}
	kbd {
		font-size: var(--text-xs);
		color: var(--text-muted);
		border: 1px solid var(--border);
		border-radius: var(--radius-sm);
		padding: 1px 5px;
	}
	ul {
		list-style: none;
		margin: 0;
		padding: var(--space-2);
		overflow-y: auto;
	}
	.section-label {
		padding: var(--space-2) var(--space-2) var(--space-1);
		font-size: var(--text-xs);
		font-weight: 700;
		text-transform: uppercase;
		letter-spacing: 0.04em;
		color: var(--text-muted);
	}
	button {
		display: flex;
		align-items: center;
		gap: var(--space-3);
		width: 100%;
		font: inherit;
		font-size: var(--text-sm);
		text-align: left;
		padding: var(--space-2) var(--space-3);
		border: none;
		border-radius: var(--radius-sm);
		background: none;
		color: var(--text-primary);
		cursor: pointer;
	}
	button.active {
		background: var(--accent-soft);
		color: var(--accent);
	}
	.label {
		flex: 1;
		min-width: 0;
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
		display: flex;
		flex-direction: column;
	}
	.snippet {
		font-size: var(--text-xs);
		font-weight: 400;
		color: var(--text-muted);
		overflow: hidden;
		text-overflow: ellipsis;
		white-space: nowrap;
	}
	.hint {
		flex-shrink: 0;
		font-size: var(--text-xs);
		color: var(--text-muted);
	}
	.empty {
		padding: var(--space-4);
		text-align: center;
		color: var(--text-muted);
		font-size: var(--text-sm);
	}
	@keyframes fade {
		from {
			opacity: 0;
		}
		to {
			opacity: 1;
		}
	}
	@keyframes rise {
		from {
			opacity: 0;
			transform: translateY(-8px) scale(0.98);
		}
		to {
			opacity: 1;
			transform: translateY(0) scale(1);
		}
	}
</style>
